/**
 * NutritionContext — Single source of truth for current-day nutrition totals.
 *
 * Fetches from the same endpoints as the Track page:
 *   GET /api/food-log/summary?date=today → consumed { calories, protein, carbs, fat }
 *   GET /api/macros                      → targets  { calories, protein, carbs, fat }
 *
 * Both Dashboard and Track page read from this context.
 * Call refresh() after any food-log mutation to keep both in sync.
 */
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NutritionContext = createContext(null);

export const useNutrition = () => useContext(NutritionContext);

export const NutritionProvider = ({ children }) => {
  const { user } = useAuth();
  const [consumed, setConsumed] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [targets, setTargets] = useState({ calories: 2000, protein: 150, carbs: 200, fat: 70 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const today = format(new Date(), "yyyy-MM-dd");
    try {
      const [summaryRes, macrosRes] = await Promise.all([
        apiClient.get(`/food-log/summary?date=${today}`),
        apiClient.get("/macros"),
      ]);
      setConsumed(summaryRes.data);
      const m = macrosRes.data;
      setTargets({
        calories: m.daily_calories || 2000,
        protein: m.protein_grams || 150,
        carbs: m.carbs_grams || 200,
        fat: m.fat_grams || 70,
      });
    } catch (e) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  /* Auto-refresh when tab regains visibility */
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  /* Memoize remaining to prevent new object on every render */
  const remaining = useMemo(() => ({
    calories: Math.round(targets.calories - consumed.calories),
    protein: Math.round(targets.protein - consumed.protein),
    carbs: Math.round(targets.carbs - consumed.carbs),
    fat: Math.round(targets.fat - consumed.fat),
  }), [targets, consumed]);

  /* Stable context value — only changes when data actually changes */
  const value = useMemo(
    () => ({ consumed, targets, remaining, loading, refresh }),
    [consumed, targets, remaining, loading, refresh]
  );

  return (
    <NutritionContext.Provider value={value}>
      {children}
    </NutritionContext.Provider>
  );
};
